import { z } from "zod";
import { paged, uuid } from "../_common.js";

/**
 * `buyer` is a storefront account, not a workspace user. Buyer-initiated
 * entries have been in `audit_entries` since checkout shipped; leaving them out
 * of this enum meant the audit log's own output schema would reject a page
 * containing one.
 */
export const auditActorType = z.enum(["web", "whatsapp", "api", "system", "ai", "buyer"]);

export const auditCategory = z.enum([
  "records",
  "money",
  "compliance",
  "settings",
  "auth",
  "integrations",
  "ai",
  "system",
]);

export const auditSeverity = z.enum(["good", "info", "warn", "rose", "violet", "neutral"]);

export const listInput = z.object({
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(50),
  query: z.string().trim().max(120).optional(),
  category: auditCategory.optional(),
  actorType: auditActorType.optional(),
  entityType: z.string().trim().min(1).max(64).optional(),
  /** Scope the feed to a single entity (e.g. one Style File's activity). */
  entityId: z.string().trim().min(1).max(64).optional(),
  verb: z.string().trim().min(1).max(64).optional(),
});

export const auditRow = z.object({
  id: uuid,
  actorType: auditActorType,
  actorId: z.string(),
  actorName: z.string().nullable(),
  actorRole: z.string().nullable(),
  entityType: z.string(),
  entityId: z.string(),
  verb: z.string(),
  category: auditCategory,
  severity: auditSeverity,
  beforeHash: z.string().nullable(),
  afterHash: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()),
  at: z.string().datetime(),
});

export const listOutput = paged(auditRow).extend({
  kpis: z.object({
    total30d: z.number().int().nonnegative(),
    today: z.number().int().nonnegative(),
    sensitiveReads30d: z.number().int().nonnegative(),
    integration30d: z.number().int().nonnegative(),
  }),
  topActors: z.array(
    z.object({
      actorType: auditActorType,
      actorId: z.string(),
      actorName: z.string().nullable(),
      actorRole: z.string().nullable(),
      count: z.number().int().nonnegative(),
    }),
  ),
  topObjects: z.array(
    z.object({
      entityType: z.string(),
      count: z.number().int().nonnegative(),
    }),
  ),
  recentSignIns: z.array(auditRow),
  availableEntityTypes: z.array(z.string()),
  availableVerbs: z.array(z.string()),
});

export type AuditActorType = z.infer<typeof auditActorType>;
export type AuditCategory = z.infer<typeof auditCategory>;
export type AuditSeverity = z.infer<typeof auditSeverity>;
export type ListInput = z.infer<typeof listInput>;
export type AuditRow = z.infer<typeof auditRow>;
export type ListOutput = z.infer<typeof listOutput>;
